import { BackofficeARIDTO, DailySet, ValueTypes } from "../types";
import { InventoryLine } from "../../../../gen/witbooking/backoffice/inventory/v1/inventory_api_pb";
import { InventoryConfiguration } from "./InventoryConfiguration";

interface GetInventoryNameRequest {
  accommodation?: string;
  configuration?: string;
  mealPlan?: string;
  condition?: string;
  ticker: string;
}

interface DailyValuesProps {
  [key: string]: {
    dailySet: DailySet[];
    parentTicker?: string;
    formula?: string;
  };
}

interface InventoryState {
  [key: string]: {
    parentTicker?: string;
    own?: string;
    formula?: string;
    parametrizedFormula?: {
      immutableFormula?: string;
      rateOwner?: string;
    };
  };
}

const getInventoryName = ({
  accommodation,
  configuration,
  mealPlan,
  condition,
  ticker,
}: GetInventoryNameRequest): string => {
  if (!accommodation || !configuration || !mealPlan || !condition) {
    return ticker;
  }
  return `${accommodation}, ${configuration}, ${mealPlan}, ${condition} (${ticker})`;
};

export class InventoryData {
  ticker: string;
  name: string;
  configuration: InventoryConfiguration;
  dailyValues: {
    [key in ValueTypes]?: {
      dailySet: DailySet[];
      parentTicker: string;
      formula?: string;
      parametrizedFormula?: {
        immutableFormula?: string;
        rateOwner?: string;
      };
    };
  };
  isChild: boolean;

  private constructor(
    ticker: string,
    name: string,
    configuration: InventoryConfiguration,
    dailyValues: {
      [key in ValueTypes]?: {
        dailySet: DailySet[];
        parentTicker: string;
        formula?: string;
        parametrizedFormula?: {
          immutableFormula?: string;
          rateOwner?: string;
        };
      };
    },
    isChild: boolean
  ) {
    this.ticker = ticker;
    this.name = name;
    this.configuration = configuration;
    this.dailyValues = dailyValues;
    this.isChild = isChild;
  }

  static of(
    backofficeAriDto: BackofficeARIDTO,
    inventoryLinesList: InventoryLine.AsObject[],
    currentInventoryState: InventoryState[]
  ): InventoryData[] {
    // TODO: temporary, this should not be in a try/catch
    try {
      return currentInventoryState.map(state => {
        const ticker = state.ticker as string;
        const inventoryLine = inventoryLinesList.find(
          it => it.code === ticker
        ) as InventoryLine.AsObject;
        const filteredBackofficeARI: DailyValuesProps = Object.entries(state)
          .filter(
            ([valueType, values]: any) =>
              valueType !== "promoRate" &&
              (values.parentTicker ||
                values.own === "Propia" ||
                values.formula ||
                values.parametrizedFormula)
          )
          .reduce((acc, [valueType, values]: any) => {
            const resultValueType = valueType === "lock" ? ValueTypes.closed : valueType;
            return {
              ...acc,
              [resultValueType]: {
                ...backofficeAriDto[ticker][resultValueType as ValueTypes],
                parentTicker: values.parentTicker,
                formula: values.formula,
                parametrizedFormula: values.parametrizedFormula,
              },
            };
          }, {});
        const isChild = Object.values(filteredBackofficeARI).every(it => it && it.parentTicker);
        return new InventoryData(
          ticker,
          getInventoryName({
            accommodation: inventoryLine?.accommodation?.name,
            configuration: inventoryLine?.configuration?.name,
            mealPlan: inventoryLine?.mealPlan?.name,
            condition: inventoryLine?.condition?.name,
            ticker,
          }),
          {
            accommodation: inventoryLine?.accommodation?.ticker || "",
            condition: inventoryLine?.condition?.ticker || "",
            mealPlan: inventoryLine?.mealPlan?.ticker || "",
            occupancy: inventoryLine?.configuration?.ticker || "",
          },
          filteredBackofficeARI,
          isChild
        );
      });
    } catch (exception) {
      console.error("Error generating InventoryData", exception);
    }
    return [];
  }
}
